import { useTranslation } from 'react-i18next';
import { useState, type FC } from 'react';
import {
  VOICE_CALLS_PER_PAGE,
  useGetVoiceCalls,
} from 'infrastructure/hooks/ccm';
import BaseTableNew from 'components/atoms/base-table/new';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import { ModuleType } from 'infrastructure/enums';
import classNames from 'classnames';
import { useModule } from 'infrastructure/providers/module-provider';
import { openInNewTab } from 'utils';
import { useGetCallRecordLink } from 'infrastructure/hooks/calls/use-get-call-record-link';
import { PhoneOutlined } from '@ant-design/icons';

import s from './styles.module.scss';
import { columns } from './columns';
import ModuleTabs from '../module-tabs';

import type { SortState } from 'infrastructure/types';

interface VoiceCallsLoggedProps {
  patientGuid: string;
  module?: ModuleType;
  showTabs?: boolean;
  showTitle?: boolean;
}

const VoiceCallsLogged: FC<VoiceCallsLoggedProps> = ({
  patientGuid,
  module = ModuleType.CCM,
  showTabs,
  showTitle = true,
}) => {
  const [page, setPage] = useState(1);
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const [createdAtSort, setCreatedAtSort] = useState<SortState>('');

  const { module: internalModule, changeModule: setModuleType } =
    useModule(module);
  const moduleType = showTabs ? internalModule : module;

  const [downloadedCallId, setDownloadedCallId] = useState<string>();

  const { calls, isLoading } = useGetVoiceCalls({
    patientGuid,
    documentationTimeSort,
    createdAtSort,
    meta: { items: VOICE_CALLS_PER_PAGE, page },
    module: moduleType,
  });
  const { getCallRecordLink, getCallRecordLinkLoading } =
    useGetCallRecordLink();
  const { t } = useTranslation();

  const onDownload = async (callId: string) => {
    setDownloadedCallId(callId);
    const url = await getCallRecordLink(
      { callId, patientGuid, module: moduleType },
      {
        onSuccess() {
          setDownloadedCallId(undefined);
        },
      },
    );
    if (url) {
      openInNewTab(url);
    }
  };

  return (
    <div className={s.wrapper}>
      <div className={classNames({ [s['title-wrapper']]: showTabs })}>
        {showTitle && (
          <h3 className={s.title} data-cy="voice-calls-section">
            {t('labels.voiceCallsLogged')}
          </h3>
        )}
        {showTabs && (
          <ModuleTabs
            activeKey={moduleType}
            className={s.tabs}
            onChange={(tab) => {
              setModuleType(tab.key);
              setPage(1);
              setDocumentationTimeSort('');
              setCreatedAtSort('');
            }}
          />
        )}
      </div>
      <BaseTableNew
        key={moduleType}
        items={calls?.items ?? []}
        columns={columns({
          onDownload,
          downloadedCallId,
          downloadLoading: getCallRecordLinkLoading,
          documentationTimeSort,
          createdAtSort,
        })}
        bordered
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: VOICE_CALLS_PER_PAGE,
          total: calls?.count ?? 0,
          position: ['bottomRight'],
        }}
        onChangePage={(newPage) => {
          setPage(newPage);
        }}
        dataCy="voice-calls-table"
        emptyText={
          <EmptyTablePlaceholder
            icon={<PhoneOutlined />}
            text={t('labels.noVoiceCallsLogged')}
            column
          />
        }
        changeOddRowColor={false}
        rowHoverable={false}
        onSort={(field, order) => {
          if (!field || !order) {
            setCreatedAtSort('');
            setDocumentationTimeSort('');
          } else if (field === 'createdAt') {
            setCreatedAtSort(order);
            setDocumentationTimeSort('');
          } else if (field === 'documentationTime') {
            setDocumentationTimeSort(order);
            setCreatedAtSort('');
          }
        }}
      />
    </div>
  );
};
export default VoiceCallsLogged;
